<template>
	<div class="dialog draggable-item bg-light light w-100-sm w-100-md w-75-lg w-50-xl" v-cloak @keyup.esc="close">
		<div class="dialog-title pl-10">
			{{ resultsMode ? (deploymentDetails.name || deploymentForm.deploy.name) + ' - ' + $t('Details') : $t('Create script deployment') }}
			<span v-if="!resultsMode && scriptLocked"> - {{ selectedScriptName }}</span>
			<span class="button square closer" @click="close"></span>
		</div>

		<div class="dialog-content overflow bg-light">
			<template v-if="resultsMode">
				<div class="mb-4">
					<div><b>{{ $t('Name') }}:</b> {{ deploymentDetails.name }}</div>
					<div><b>{{ $t('Script') }}:</b> {{ deploymentScriptName }}</div>
					<div><b>{{ $t('Devices') }}:</b> {{ deploymentComputerNames.join(', ') }}</div>
					<div v-if="deploymentDetails.id_user"><b>{{ $t('Created by') }}:</b> {{ $root.userDisplayName(deploymentDetails.id_user) }}</div>
					<div v-if="deploymentDetails.created_at"><b>{{ $t('Created at') }}:</b> {{ formatDate(deploymentDetails.created_at) }}</div>
				</div>

				<div class="table-container">
					<table class="table border striped compact m-0 bg-white">
						<thead>
							<tr>
								<th>{{ $t('Started at') }} / {{ $t('Ended at') }}</th>
								<th>{{ $t('Result') }}</th>
								<th>{{ $t('Computer') }}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="resultsLoading">
								<td colspan="4" class="text-center p-5"><span class="mif-spinner2 fg-primary ani-spin mif-3x"></span></td>
							</tr>
							<tr v-else-if="results.length === 0">
								<td colspan="4"><NoDataAvailable :message="$t('No results available')" /></td>
							</tr>
							<template v-for="result in results" v-else>
								<tr>
									<td><p>{{ formatDate(result.executed_at) }}</p><p>{{ formatDate(result.ended_at) }}</p></td>
									<td><span class="badge inline" :class="resultBadgeClass(result.result)">{{ resultLabel(result.result) }}</span></td>
									<td>{{ computerName(result.id_computer) }}</td>
									<td class="text-right">
										<button v-if="result.details" type="button" class="button flat-button square small" :title="$t('Details')" @click="toggleResultDetails(result.id)">
											<span :class="isResultDetailsExpanded(result.id) ? 'mif-chevron-thin-up' : 'mif-chevron-thin-down'"></span>
										</button>
									</td>
								</tr>
								<tr v-if="result.details && isResultDetailsExpanded(result.id)" :key="result.id + '-details'" class="deploy-result-details-row">
									<td colspan="4"><div class="deploy-result-details">{{ result.details }}</div></td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</template>

			<template v-else>
				<div v-if="selectedScriptIsDraft" class="remark alert">{{ $t('Draft scripts cannot be deployed') }}</div>

				<div class="form-group">
					<label class="text-bold">{{ $t('Name') }}*</label>
					<input v-model.trim="deploymentForm.deploy.name" class="input" type="text" maxlength="255" :placeholder="$t('Deployment name')" />
				</div>

				<div v-if="!scriptLocked" class="form-group">
					<label class="text-bold">{{ $t('Script') }}*</label>
					<select v-model="deploymentForm.deploy.id_script" class="select input w-100">
						<option value="" disabled>{{ $t('Select a script') }}</option>
						<option v-for="script in scripts" :key="script.id" :value="script.id">{{ script.name }}</option>
					</select>
				</div>

				<div class="form-group">
					<label class="text-bold">{{ $t('Devices') }}*</label>
					<div class="d-flex flex-align-center flex-justify-between mb-2">
						<div class="text-small fg-gray">{{ $t('Only computers currently connected are displayed.') }}</div>
						<button type="button" class="button flat-button square small" :title="$t('Refresh')" @click="refreshComputerTree"><span class="mif-loop2"></span></button>
					</div>
					<div v-if="onlineComputers.length" class="script-deploy-computers border bg-white p-2">
						<ul v-if="computerGroups.length" class="script-deploy-tree m-0">
							<li v-for="group in computerGroups" :key="group.id_group">
								<details open>
									<summary class="text-bold p-1">
										<input type="checkbox" :checked="isGroupSelected(group)" :indeterminate.prop="isGroupPartiallySelected(group)" @click.stop @change="toggleGroup(group, $event.target.checked)" />
										<span class="ml-1">{{ group.name_group }}</span>
									</summary>
									<ul>
										<li v-for="computer in group.computers" :key="computer.id">
											<label class="d-flex flex-align-center p-1 script-deploy-computer">
												<input type="checkbox" :checked="isComputerSelected(computer.id)" @change="toggleComputer(computer.id, $event.target.checked)" />
												<span class="ml-2">{{ computer.pc_name || computer.name }}</span>
											</label>
										</li>
									</ul>
								</details>
							</li>
						</ul>
						<NoDataAvailable v-else :message="$t('No devices available')" />
					</div>
					<NoDataAvailable v-else :message="$t('No devices available')" />
				</div>
			</template>
		</div>

		<div class="dialog-actions">
			<ClientMessageView :commands="['scriptdeploymethods']" />
			<button v-if="resultsMode" class="button secondary" :disabled="selectedScriptIsDraft" @click="prepareRepeatDeployment">{{ $t('Repeat deploy') }}</button>
			<button v-else class="button secondary" :disabled="!canCreate || saving" @click="createDeployment">
				<span v-if="saving" class="mif-spinner2 ani-spin mr-1"></span>{{ $t('Create') }}
			</button>
			<button class="button ml-2" :disabled="saving" @click="close">{{ $t('Close') }}</button>
		</div>
	</div>
</template>

<script>
import { draggableMixin } from '../../mixins/draggable-mixin';
import ClientMessageView from './view/ClientMessageView.vue';
import NoDataAvailable from '../utils/NoDataAvailable.vue';
import { ScriptsHelper } from '../../utils/objects';

export default {
	name: 'ScriptDeploy',
	mixins: [draggableMixin],
	components: { ClientMessageView, NoDataAvailable },
	props: {
		scriptDeployment: { type: Object, required: true }
	},
	data() {
		const source = JSON.parse(JSON.stringify(this.scriptDeployment));
		const resultsMode = !!source.id && !source.deploy;
		const deploymentForm = source.deploy ? source : {
			deploy: { id_script: source.id_script || '', name: source.name || '' },
			computer_ids: [...new Set(source.computer_ids || [])]
		};
		const scriptLocked = !!deploymentForm.deploy.id_script;
		const selectedScript = null;

		return {
			deploymentForm,
			deploymentDetails: resultsMode ? source : {},
			deploymentId: resultsMode ? source.id : null,
			resultsMode,
			results: [],
			resultsLoading: resultsMode,
			resultsRequestRunning: false,
			resultsRefreshTimer: null,
			expandedResultIds: [],
			deploymentComputerNames: [],
			deploymentScriptName: selectedScript ? selectedScript.name : deploymentForm.deploy.id_script,
			scripts: [],
			allScripts: [],
			onlineComputers: [],
			computerGroups: [],
			scriptLocked,
			selectedScriptIsDraft: !!selectedScript && selectedScript.status === 'draft',
			selectedScriptName: selectedScript ? selectedScript.name : this.$t('Script'),
			saving: false,
			isClosing: false
		};
	},
	computed: {
		canCreate() {
			return !this.selectedScriptIsDraft && !!this.deploymentForm.deploy.id_script &&
				!!this.deploymentForm.deploy.name && this.getSelectedOnlineComputerIds().length > 0;
		}
	},
	async created() {
		this.refreshComputerTree();
		this.allScripts = await this.$api.getScripts();
		this.scripts = this.allScripts.filter(script => script.status !== 'draft');
		const selectedScript = this.getScriptWithID(this.deploymentForm.deploy.id_script);
		if (selectedScript) {
			this.deploymentScriptName = selectedScript.name;
			this.selectedScriptIsDraft = selectedScript.status === 'draft';
			this.selectedScriptName = selectedScript.name;
		}
		if (this.resultsMode) {
			await this.loadResults();
			this.startResultsRefresh();
		}
	},
	beforeDestroy() {
		this.stopResultsRefresh();
	},
	methods: {
		getScriptWithID(idScript) {
			return this.allScripts.find(script => script.id == idScript) || null;
		},
		refreshComputerTree() {
			this.onlineComputers = this.$session.getPCList().filter(computer => this.$root.connectionsStates[computer.id] == true);
			const assignedComputerIds = new Set();
			this.computerGroups = this.$session.getGroups().map(group => {
				const computers = this.onlineComputers.filter(computer => this.computerBelongsToGroup(computer, group));
				computers.forEach(computer => assignedComputerIds.add(computer.id));
				return { ...group, computers };
			}).filter(group => group.computers.length > 0);
			const otherComputers = this.onlineComputers.filter(computer => !assignedComputerIds.has(computer.id));
			if (otherComputers.length) this.computerGroups.push({ id_group: '__other_online_computers__', name_group: this.$t('Other devices'), computers: otherComputers });
		},
		computerBelongsToGroup(computer, group) {
			const idGroup = group.id_group != null ? group.id_group : group.id;
			const computerGroupIds = [].concat(computer.groups || [], computer.ids_groups || [], computer.id_groups || [], computer.group_ids || [])
				.map(value => value && typeof value === 'object' ? (value.id_group != null ? value.id_group : (value.group_id != null ? value.group_id : value.id)) : value);
			if (computer.id_group != null) computerGroupIds.push(computer.id_group);
			if (computerGroupIds.some(id => id == idGroup)) return true;
			const groupComputers = [].concat(group.computers || [], group.ids_computers || [], group.computer_ids || [], group.pcs || [], group.ids_pcs || []);
			return groupComputers.some(value => {
				const idComputer = value && typeof value === 'object' ? (value.id_computer != null ? value.id_computer : (value.id_pc != null ? value.id_pc : value.id)) : value;
				return idComputer == computer.id;
			});
		},
		getSelectedOnlineComputerIds() {
			const onlineIds = new Set(this.onlineComputers.map(computer => computer.id));
			return [...new Set(this.deploymentForm.computer_ids || [])].filter(id => onlineIds.has(id));
		},
		isComputerSelected(idComputer) { return (this.deploymentForm.computer_ids || []).includes(idComputer); },
		toggleComputer(idComputer, selected) {
			const ids = new Set(this.deploymentForm.computer_ids || []);
			selected ? ids.add(idComputer) : ids.delete(idComputer);
			this.deploymentForm.computer_ids = [...ids];
		},
		isGroupSelected(group) { return group.computers.length > 0 && group.computers.every(computer => this.isComputerSelected(computer.id)); },
		isGroupPartiallySelected(group) {
			const count = group.computers.filter(computer => this.isComputerSelected(computer.id)).length;
			return count > 0 && count < group.computers.length;
		},
		toggleGroup(group, selected) {
			const ids = new Set(this.deploymentForm.computer_ids || []);
			group.computers.forEach(computer => selected ? ids.add(computer.id) : ids.delete(computer.id));
			this.deploymentForm.computer_ids = [...ids];
		},
		async createDeployment() {
			if (!this.canCreate || this.saving) return;
			this.saving = true;
			try {
				this.deploymentForm.computer_ids = this.getSelectedOnlineComputerIds();
				const created = await this.$api.createScriptDeploy(this.deploymentForm);
				if (!created) return;
				this.deploymentId = typeof created === 'string' ? created : (created.id || created.id_deploy);
				if (!this.deploymentId) return;
				this.resultsMode = true;
				this.resultsLoading = true;
				await this.loadResults();
				this.startResultsRefresh();
				this.$root.$emit('REFRESHSCRIPTDEPLOYHISTORY');
			} finally {
				this.saving = false;
			}
		},
		async loadResults() {
			if (!this.deploymentId || this.resultsRequestRunning || this.isClosing) return;
			this.resultsRequestRunning = true;
			try {
				const deployment = await this.$api.getScriptDeployResults(this.deploymentId);
				if (!deployment) return;
				this.deploymentDetails = deployment;
				this.deploymentId = deployment.id || this.deploymentId;
				this.results = Array.isArray(deployment.results) ? deployment.results : [];
				this.deploymentScriptName = this.scriptName(deployment.id_script);
				this.deploymentComputerNames = [...new Set(deployment.computer_ids || [])].map(this.computerName);
			} finally {
				this.resultsLoading = false;
				this.resultsRequestRunning = false;
			}
		},
		prepareRepeatDeployment() {
			this.stopResultsRefresh(false);
			const idScript = this.deploymentDetails.id_script;
			const script = this.getScriptWithID(idScript);
			if (script && script.status === 'draft') return;
			this.deploymentForm = {
				deploy: { id_script: idScript, name: this.deploymentDetails.name },
				computer_ids: [...new Set(this.deploymentDetails.computer_ids || [])]
			};
			this.scriptLocked = true;
			this.selectedScriptIsDraft = false;
			this.selectedScriptName = script ? script.name : idScript;
			this.resultsMode = false;
			this.expandedResultIds = [];
			this.refreshComputerTree();
		},
		startResultsRefresh() {
			this.isClosing = false;
			this.stopResultsRefresh(false);
			if (!this.isClosing && this.resultsMode) this.resultsRefreshTimer = setInterval(this.loadResults, 20 * 1000);
		},
		stopResultsRefresh(markClosing = true) {
			if (markClosing) this.isClosing = true;
			if (this.resultsRefreshTimer != null) {
				clearInterval(this.resultsRefreshTimer);
				this.resultsRefreshTimer = null;
			}
		},
		scriptName(idScript) {
			const script = this.getScriptWithID(idScript);
			return script ? script.name : idScript;
		},
		computerName(idComputer) {
			const computer = this.$session.getPCWithID(idComputer) || (this.$root.pcMap && this.$root.pcMap[idComputer]);
			return computer ? (computer.pc_name || computer.name) : idComputer;
		},
		resultLabel(result) {
			const key = ScriptsHelper.scriptDeployResult[result];
			return key ? this.$t(key) : result;
		},
		resultBadgeClass(result) {
			return { pending: 'deploy-result-pending', deployed: 'deploy-result-deployed', success: 'deploy-result-success', failed: 'deploy-result-error', timeout: 'deploy-result-warning', skipped: 'deploy-result-warning', error: 'deploy-result-error' }[result] || 'deploy-result-unknown';
		},
		isResultDetailsExpanded(id) { return this.expandedResultIds.includes(id); },
		toggleResultDetails(id) {
			this.expandedResultIds = this.isResultDetailsExpanded(id) ? this.expandedResultIds.filter(value => value !== id) : [...this.expandedResultIds, id];
		},
		formatDate(value) {
			if (!value) return '';
			return typeof value.toLocalDate === 'function' ? value.toLocalDate() : new Date(value).toLocaleString();
		},
		close() {
			this.stopResultsRefresh();
			this.$root.$emit('CLOSESCRIPTDEPLOYDIALOG');
		}
	}
};
</script>

<style scoped>
.script-deploy-computers { max-height: 260px; overflow-y: auto; }
.script-deploy-computer { cursor: pointer; margin: 0; }
.script-deploy-computer:hover { background-color: #f4f6f8; }
.script-deploy-tree, .script-deploy-tree ul { list-style: none; padding-left: 18px; }
.script-deploy-tree > li { padding-left: 0; }
.script-deploy-tree summary { cursor: pointer; }
.deploy-result-details { white-space: pre-wrap; tab-size: 4; word-break: break-word; font-family: "Cascadia Mono", "Consolas", "Courier New", monospace; }
.deploy-result-details-row > td { background-color: #f8fafc; }
.deploy-result-pending, .deploy-result-unknown { background-color: #f8fafc; border-color: #e2e8f0; color: #475569; }
.deploy-result-deployed { background-color: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
.deploy-result-success { background-color: #f0fdf4; border-color: #bbf7d0; color: #16a34a; }
.deploy-result-warning { background-color: #fffbeb; border-color: #fde68a; color: #d97706; }
.deploy-result-error { background-color: #fef2f2; border-color: #fecaca; color: #dc2626; }
</style>
